import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Eye, FileText, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/auth";
import { downloadDiscoveryPdf } from "@/lib/downloads";

const inputCls = "w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const DEFAULT_FILTERS = { search: "", archetype: "", cluster: "", min_apt: 0, max_apt: 100 };

export default function DiscoveryAdmin() {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (activeFilters) => {
    setBusy(true);
    const params = new URLSearchParams();
    Object.entries(activeFilters).forEach(([k, v]) => { if (v !== "" && v !== null) params.set(k, v); });
    const res = await authFetch(`/api/admin/discovery/reports?${params}`);
    if (res.ok) setRows(await res.json());
    else toast.error("Could not load Discovery results");
    setBusy(false);
  }, []);

  useEffect(() => { load(DEFAULT_FILTERS); }, [load]);

  const archetypes = useMemo(() => [...new Set((rows || []).map((r) => r.archetype).filter(Boolean))].sort(), [rows]);
  const clusters = useMemo(() => [...new Set((rows || []).map((r) => r.top_cluster).filter(Boolean))].sort(), [rows]);
  const avgApt = rows?.length ? Math.round(rows.reduce((sum, r) => sum + r.aptitude_overall, 0) / rows.length) : 0;
  const flagged = (rows || []).filter((r) => (r.quality?.very_fast_answers || 0) > 10 || (r.quality?.longest_same_answer_run || 0) >= 20).length;

  const openDetail = async (row) => {
    const res = await authFetch(`/api/discovery/report/${row.id}`);
    if (res.ok) setDetail(await res.json());
    else toast.error("Could not load member detail");
  };

  const exportCsv = async () => {
    const res = await authFetch("/api/admin/export/discovery");
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "brightiq-discovery.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Discovery CSV downloaded");
  };

  const downloadPdf = async (row) => {
    try {
      await downloadDiscoveryPdf(row.id, `brightiq-discovery-${row.name.toLowerCase().replace(/\s+/g, "-")}.pdf`);
    } catch (err) {
      toast.error(err.message || "PDF download failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="tab-discovery">
      <div className="grid sm:grid-cols-3 gap-4">
        {[[rows?.length ?? "…", "Filtered results"], [rows ? `${avgApt}%` : "…", "Average aptitude"], [rows ? flagged : "…", "Quality flags"]].map(([v, l]) => (
          <div key={l} className="rounded-3xl bg-cream border border-ink/10 p-6">
            <div className="font-display font-bold text-3xl">{v}</div>
            <div className="text-xs text-ink/50 mt-1">{l}</div>
          </div>
        ))}
      </div>

      <div className="rounded-3xl bg-cream border border-ink/10 p-5">
        <div className="grid md:grid-cols-[1.2fr_1fr_1fr_0.6fr_0.6fr_auto] gap-3" data-testid="discovery-admin-filters">
          <label className="relative"><Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/35" /><input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Search name or email" className={`${inputCls} pl-10`} data-testid="discovery-admin-search" /></label>
          <select value={filters.archetype} onChange={(e) => setFilters({ ...filters, archetype: e.target.value })} className={inputCls} data-testid="discovery-admin-archetype-filter"><option value="">All archetypes</option>{archetypes.map((a) => <option key={a}>{a}</option>)}</select>
          <select value={filters.cluster} onChange={(e) => setFilters({ ...filters, cluster: e.target.value })} className={inputCls} data-testid="discovery-admin-cluster-filter"><option value="">All top clusters</option>{clusters.map((c) => <option key={c}>{c}</option>)}</select>
          <input type="number" min="0" max="100" value={filters.min_apt} onChange={(e) => setFilters({ ...filters, min_apt: Number(e.target.value) })} className={inputCls} data-testid="discovery-admin-min-apt" aria-label="Minimum aptitude" />
          <input type="number" min="0" max="100" value={filters.max_apt} onChange={(e) => setFilters({ ...filters, max_apt: Number(e.target.value) })} className={inputCls} data-testid="discovery-admin-max-apt" aria-label="Maximum aptitude" />
          <div className="flex gap-2">
            <button onClick={() => load(filters)} disabled={busy} data-testid="discovery-admin-apply" className="bg-ink text-paper text-xs font-semibold px-5 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}</button>
            <button onClick={exportCsv} data-testid="discovery-admin-export" className="flex items-center gap-1.5 border border-ink/15 text-xs font-semibold px-4 rounded-full hover:bg-ink hover:text-paper transition-colors"><Download className="h-3.5 w-3.5" /> CSV</button>
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-cream border border-ink/10 overflow-hidden" data-testid="discovery-admin-table">
        <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-sm tracking-tight">Discovery reports <span className="text-ink/40 font-body font-medium">({rows?.length ?? "…"})</span></div>
        {!rows ? <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-iqblue" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-ink/45 uppercase tracking-wider">{["Member", "Archetype", "Top cluster", "Aptitude", "Quality", "When", "Actions"].map((c) => <th key={c} className="px-6 py-3 font-semibold">{c}</th>)}</tr></thead>
              <tbody>
                {rows.map((r, i) => {
                  const flag = (r.quality?.very_fast_answers || 0) > 10 || (r.quality?.longest_same_answer_run || 0) >= 20;
                  return (
                    <tr key={r.id} className="border-t border-ink/5 hover:bg-white/60 transition-colors" data-testid={`discovery-admin-row-${i}`}>
                      <td className="px-6 py-3 whitespace-nowrap"><b>{r.name}</b><div className="text-ink/45 mt-0.5">{r.email}</div></td>
                      <td className="px-6 py-3 whitespace-nowrap">{r.archetype}<div className="text-ink/45 mt-0.5">{r.secondary}</div></td>
                      <td className="px-6 py-3 whitespace-nowrap">{r.top_cluster} <b>{r.top_fit}%</b></td>
                      <td className="px-6 py-3 whitespace-nowrap font-bold">{r.aptitude_overall}%</td>
                      <td className="px-6 py-3 whitespace-nowrap"><span className={`rounded-full px-2.5 py-1 font-bold ${flag ? "bg-iqpink/10 text-iqpink" : "bg-iqgreen/10 text-iqgreen"}`}>{flag ? "Review" : "Clear"}</span></td>
                      <td className="px-6 py-3 whitespace-nowrap text-ink/50">{fmtDate(r.created_at)}</td>
                      <td className="px-6 py-3 whitespace-nowrap"><div className="flex gap-1.5"><button onClick={() => openDetail(r)} data-testid={`discovery-admin-view-${i}`} className="h-8 w-8 rounded-full border border-ink/15 flex items-center justify-center hover:bg-iqblue hover:text-paper transition-colors"><Eye className="h-3.5 w-3.5" /></button><button onClick={() => downloadPdf(r)} data-testid={`discovery-admin-pdf-${i}`} className="h-8 w-8 rounded-full border border-ink/15 flex items-center justify-center hover:bg-iqamber transition-colors"><FileText className="h-3.5 w-3.5" /></button></div></td>
                    </tr>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan="7" className="px-6 py-8 text-ink/40">No Discovery reports match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <div className="rounded-3xl bg-ink text-paper p-7" data-testid="discovery-admin-detail">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-iqcyan">Member detail</div>
              <h3 className="font-display font-bold text-2xl tracking-tight mt-2">{detail.name} · {detail.archetype}</h3>
              <p className="text-xs text-paper/55 mt-1">{detail.email} · {detail.grade || "—"} · {detail.study_location || "—"}</p>
            </div>
            <button onClick={() => setDetail(null)} data-testid="discovery-admin-detail-close" className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"><X className="h-4 w-4" /></button>
          </div>
          <div className="mt-6 grid md:grid-cols-4 gap-3">
            {[[detail.aptitude_overall + "%", "Aptitude"], [detail.secondary, "Secondary"], [detail.clusters?.[0]?.name, "Top cluster"], [detail.clusters?.[0]?.fit + "%", "Cluster fit"]].map(([v, l]) => <div key={l} className="rounded-2xl bg-white/10 border border-white/10 p-4"><div className="font-display font-bold text-lg truncate">{v}</div><div className="text-[10px] uppercase tracking-wider text-paper/45 mt-1">{l}</div></div>)}
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to={`/discovery/report/${detail.id}`} data-testid="discovery-admin-member-report" className="bg-paper text-ink text-xs font-bold px-5 py-3 rounded-full hover:bg-iqcyan transition-colors">Open member report</Link>
            <button onClick={() => downloadPdf(detail)} data-testid="discovery-admin-detail-pdf" className="bg-iqamber text-ink text-xs font-bold px-5 py-3 rounded-full hover:brightness-110 transition-all">Download PDF</button>
          </div>
        </div>
      )}
    </div>
  );
}
